import React, { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { api } from "@/services/api";
import { formatDateShort, getUserTimezone } from "@/lib/dateUtils";
import { AuthenticatedImage } from "@/components/AuthenticatedImage";
import { Copy, Eye, Download } from "lucide-react";

// Configurable polling interval (ms)
const POLL_INTERVAL_MS = 5000;
const PAGE_SIZE = 20;

// Status icon enum matching backend ProcessingStatusIconEnum
enum ProcessingStatusIconEnum {
  InProgress = "InProgress",
  Completed = "Completed",
  Failed = "Failed",
  Unknown = "Unknown",
  ReadyForReview = "ReadyForReview",
  InReview = "InReview",
}

interface Job {
  JobId: string;
  FileName: string;
  Status: string;
  StatusIcon: ProcessingStatusIconEnum;
  CreatedAt: string;
  CompletedAt?: string;
  ErrorMessage?: string | null;
  EDIContent?: string;
  ExtractedData?: {
    Shipper?: {
      Name?: string;
      Address?: string;
      City?: string;
      State?: string;
      Zip?: string;
    };
    Consignee?: {
      Name?: string;
      Address?: string;
      City?: string;
      State?: string;
      Zip?: string;
    };
    ShipmentDetails?: {
      Weight?: string;
      Pieces?: string;
      Commodity?: string;
      FreightClass?: string;
    };
    Dates?: {
      Pickup?: string;
      Delivery?: string;
    };
    References?: {
      BolNumber?: string;
      PoNumber?: string;
      OrderNumber?: string;
      ISA?: string;
    };
    ISAControlNumber?: string;
    Edi?: string;
    Validation?: string[];
    Warnings?: string[];
    MissingFields?: string[];
    ConfidenceScores?: Record<string, number>;
  };
  ImageUrls: string[];
  DisplayUrls: string[];
  ThumbnailUrls: string[];
  ISAControlNumber?: string;
  ProcessingType?: string;
  EdiInterchangeSenderId?: string;
  EdiInterchangeReceiverId?: string;
  ReviewStatus?: string | null;
  ReviewClaimedByUserEmail?: string | null;
}

const statusIcon = (
  statusIcon: ProcessingStatusIconEnum,
  statusText?: string
) => {
  switch (statusIcon) {
    case ProcessingStatusIconEnum.Completed:
      return (
        <span title="Success" style={{ color: "#059669", fontSize: 20 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              stroke="#059669"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      );
    case ProcessingStatusIconEnum.Failed:
      return (
        <span
          title="Failed [No charge]"
          style={{ color: "#dc2626", fontSize: 20 }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
              stroke="#dc2626"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      );
    case ProcessingStatusIconEnum.ReadyForReview:
      return (
        <span title="Ready for review" style={{ color: "#b45309", fontSize: 20 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              stroke="#b45309"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      );
    case ProcessingStatusIconEnum.InReview:
      return (
        <span title="In review" style={{ color: "#7c3aed", fontSize: 20 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              stroke="#7c3aed"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      );
    case ProcessingStatusIconEnum.InProgress:
      return (
        <span title="In Progress" style={{ color: "#007bff", fontSize: 20 }}>
          <svg
            className="animate-spin"
            width="20"
            height="20"
            viewBox="0 0 24 24"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="#007bff"
              strokeWidth="4"
              fill="none"
              opacity="0.2"
            />
            <path
              d="M12 2a10 10 0 0 1 10 10"
              stroke="#007bff"
              strokeWidth="4"
              fill="none"
            />
          </svg>
        </span>
      );
    case ProcessingStatusIconEnum.Unknown:
    default:
      return (
        <span
          title={statusText || "Unknown"}
          style={{ color: "gray", fontSize: 20 }}
        >
          {statusText || "Unknown"}
        </span>
      );
  }
};

// Remove the old formatDate function since we're using the new utility

const normalizeJob = (job: any): Job => {
  // Helper function to normalize nested extracted data
  const normalizeExtractedData = (data: any): Job["ExtractedData"] => {
    if (!data) return undefined;

    return {
      Shipper:
        data.Shipper || data.shipper
          ? {
              Name:
                (data.Shipper || data.shipper)?.Name ||
                (data.Shipper || data.shipper)?.name,
              Address:
                (data.Shipper || data.shipper)?.Address ||
                (data.Shipper || data.shipper)?.address,
              City:
                (data.Shipper || data.shipper)?.City ||
                (data.Shipper || data.shipper)?.city,
              State:
                (data.Shipper || data.shipper)?.State ||
                (data.Shipper || data.shipper)?.state,
              Zip:
                (data.Shipper || data.shipper)?.Zip ||
                (data.Shipper || data.shipper)?.zip,
            }
          : undefined,
      Consignee:
        data.Consignee || data.consignee
          ? {
              Name:
                (data.Consignee || data.consignee)?.Name ||
                (data.Consignee || data.consignee)?.name,
              Address:
                (data.Consignee || data.consignee)?.Address ||
                (data.Consignee || data.consignee)?.address,
              City:
                (data.Consignee || data.consignee)?.City ||
                (data.Consignee || data.consignee)?.city,
              State:
                (data.Consignee || data.consignee)?.State ||
                (data.Consignee || data.consignee)?.state,
              Zip:
                (data.Consignee || data.consignee)?.Zip ||
                (data.Consignee || data.consignee)?.zip,
            }
          : undefined,
      ShipmentDetails:
        data.ShipmentDetails || data.shipmentDetails
          ? {
              Weight:
                (data.ShipmentDetails || data.shipmentDetails)?.Weight ||
                (data.ShipmentDetails || data.shipmentDetails)?.weight,
              Pieces:
                (data.ShipmentDetails || data.shipmentDetails)?.Pieces ||
                (data.ShipmentDetails || data.shipmentDetails)?.pieces,
              Commodity:
                (data.ShipmentDetails || data.shipmentDetails)?.Commodity ||
                (data.ShipmentDetails || data.shipmentDetails)?.commodity,
              FreightClass:
                (data.ShipmentDetails || data.shipmentDetails)?.FreightClass ||
                (data.ShipmentDetails || data.shipmentDetails)?.freightClass,
            }
          : undefined,
      Dates:
        data.Dates || data.dates
          ? {
              Pickup:
                (data.Dates || data.dates)?.Pickup ||
                (data.Dates || data.dates)?.pickup,
              Delivery:
                (data.Dates || data.dates)?.Delivery ||
                (data.Dates || data.dates)?.delivery,
            }
          : undefined,
      References:
        data.References || data.references
          ? {
              BolNumber:
                (data.References || data.references)?.BolNumber ||
                (data.References || data.references)?.bolNumber,
              PoNumber:
                (data.References || data.references)?.PoNumber ||
                (data.References || data.references)?.poNumber,
              OrderNumber:
                (data.References || data.references)?.OrderNumber ||
                (data.References || data.references)?.orderNumber,
              ISA:
                (data.References || data.references)?.ISA ||
                (data.References || data.references)?.isa,
            }
          : undefined,
      ISAControlNumber: data.ISAControlNumber || data.isaControlNumber,
      Edi: data.EDIContent || data.ediContent,
      Validation: data.Validation || data.validation,
      Warnings: data.Warnings || data.warnings,
      MissingFields: data.MissingFields || data.missingFields,
      ConfidenceScores: data.ConfidenceScores || data.confidenceScores,
    };
  };

  return {
    JobId: job.JobId || job.jobId || "",
    FileName: job.FileName || job.fileName || "Unknown File",
    Status: job.Status || job.status || "Unknown",
    StatusIcon:
      job.StatusIcon !== undefined
        ? job.StatusIcon
        : job.statusIcon !== undefined
        ? job.statusIcon
        : ProcessingStatusIconEnum.InProgress,
    CreatedAt: job.CreatedAt || job.createdAt || new Date().toISOString(),
    CompletedAt: job.CompletedAt || job.completedAt,
    ErrorMessage: job.ErrorMessage || job.errorMessage,
    EDIContent: job.EDIContent || job.ediContent,
    ExtractedData: normalizeExtractedData(
      job.ExtractedData || job.extractedData
    ),
    ImageUrls: job.ImageUrls || job.imageUrls || [],
    DisplayUrls: job.DisplayUrls || job.displayUrls || [],
    ThumbnailUrls: job.ThumbnailUrls || job.thumbnailUrls || [],
    ISAControlNumber: job.ISAControlNumber || job.isaControlNumber,
    ProcessingType: job.ProcessingType || job.processingType,
    EdiInterchangeSenderId:
      job.EdiInterchangeSenderId || job.ediInterchangeSenderId,
    EdiInterchangeReceiverId:
      job.EdiInterchangeReceiverId || job.ediInterchangeReceiverId,
    ReviewStatus: job.ReviewStatus || job.reviewStatus,
    ReviewClaimedByUserEmail:
      job.ReviewClaimedByUserEmail || job.reviewClaimedByUserEmail,
  };
};

function formatJobStatusDisplay(job: Job): string {
  const status = job.Status || "Unknown";
  if (status === "ReadyForReview") {
    return "Ready for review";
  }
  if (status === "InReview") {
    if (job.ReviewClaimedByUserEmail) {
      return `In review (${job.ReviewClaimedByUserEmail})`;
    }
    return "In review";
  }
  return status;
}

const Documents: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [userTimezone, setUserTimezone] = useState(getUserTimezone());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied",
        description: "Copied to clipboard",
      });
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand("copy");
        toast({
          title: "Copied",
          description: "Copied to clipboard",
        });
      } catch (fallbackErr) {
        toast({
          title: "Error",
          description: "Unable to copy to clipboard",
          variant: "destructive",
        });
      }
      document.body.removeChild(textArea);
    }
  };

  const downloadEdi = (job: Job) => {
    if (!job) {
      toast({
        title: "Error",
        description: "No job selected.",
        variant: "destructive",
      });
      return;
    }
    if (!job.EDIContent) {
      toast({
        title: "Error",
        description: "No EDI content available to download.",
        variant: "destructive",
      });
      return;
    }
    if (!job.EdiInterchangeReceiverId) {
      toast({
        title: "Error",
        description: "Missing EDI Interchange Receiver ID.",
        variant: "destructive",
      });
      return;
    }
    if (!job.ISAControlNumber) {
      toast({
        title: "Error",
        description: "Missing ISA Control Number.",
        variant: "destructive",
      });
      return;
    }

    const sanitizeForFilename = (value: string): string => {
      return value.replace(/[\\/:*?"<>|]+/g, "_").trim();
    };

    const receiverId = sanitizeForFilename(job.EdiInterchangeReceiverId);
    const isaControl = sanitizeForFilename(job.ISAControlNumber);
    const fileName = `${receiverId}_${isaControl}.txt`;

    const blob = new Blob([job.EDIContent], {
      type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const fetchJobs = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const response = await api.getJobs({ page, pageSize: PAGE_SIZE });

      if (
        response?.success &&
        response.data?.jobs &&
        Array.isArray(response.data.jobs)
      ) {
        // Paginated format - normalize the data
        const normalizedJobs = response.data.jobs.map(normalizeJob);
        setJobs(normalizedJobs);
        setTotal(response.data.total || 0);
      } else if (response?.success && Array.isArray(response.data)) {
        // Array format - normalize the data
        const normalizedJobs = response.data.map(normalizeJob);
        setJobs(normalizedJobs);
        setTotal(normalizedJobs.length);
      } else if (response?.success && response.data) {
        // Handle case where data exists but is not in expected format
        setJobs([]);
        setTotal(0);
      } else {
        console.error("Unexpected API response format:", response);
        setJobs([]);
        setTotal(0);
        toast({ title: "Error", description: "Failed to load documents." });
      }
    } catch (error) {
      console.error("Error fetching jobs:", error);
      toast({ title: "Error", description: "Failed to load documents." });
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  // Listen for timezone changes in localStorage
  useEffect(() => {
    const handleStorageChange = () => {
      setUserTimezone(getUserTimezone());
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Polling logic
  useEffect(() => {
    fetchJobs();
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (
        jobs.some(
          (j) =>
            j.StatusIcon === ProcessingStatusIconEnum.InProgress ||
            j.StatusIcon === ProcessingStatusIconEnum.ReadyForReview ||
            j.StatusIcon === ProcessingStatusIconEnum.InReview
        )
      ) {
        fetchJobs(false); // Don't show loading spinner during polling
      }
    }, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const openModal = (job: Job) => {
    setSelectedJob(job);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedJob(null);
  };

  const openImageModal = (job: Job, imageIndex: number) => {
    setSelectedJob(job);
    setSelectedImageIndex(imageIndex);
    setImageModalOpen(true);
  };

  const closeImageModal = () => {
    setImageModalOpen(false);
    setSelectedJob(null);
    setSelectedImageIndex(0);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Processed Bills of Lading</h1>

      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="min-w-full border">
          <thead>
            <tr className="bg-gray-50">
              <th className="border px-2 py-1 text-left text-xs">Timestamp</th>
              <th className="border px-2 py-1 text-left text-xs">Thumbnails</th>
              <th className="border px-2 py-1 text-left text-xs">
                Shipper Name
              </th>
              <th className="border px-2 py-1 text-left text-xs">Sender ID</th>
              <th className="border px-2 py-1 text-left text-xs">
                Receiver ID
              </th>
              <th className="border px-2 py-1 text-left text-xs">EDI Type</th>
              <th className="border px-2 py-1 text-left text-xs">ISA</th>
              <th className="border px-2 py-1 text-left text-xs">Details</th>
              <th className="border px-2 py-1 text-left text-xs">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={9}
                  className="border px-2 py-4 text-center text-gray-500"
                >
                  <div className="flex items-center justify-center">
                    <svg
                      className="animate-spin"
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="#007bff"
                        strokeWidth="4"
                        fill="none"
                        opacity="0.2"
                      />
                      <path
                        d="M12 2a10 10 0 0 1 10 10"
                        stroke="#007bff"
                        strokeWidth="4"
                        fill="none"
                      />
                    </svg>
                    <span className="ml-2">Loading documents...</span>
                  </div>
                </td>
              </tr>
            ) : jobs.length === 0 ? (
              <tr>
                <td
                  colSpan={9}
                  className="border px-2 py-4 text-center text-gray-500"
                >
                  No processed documents found. Upload some bills of lading to
                  get started.
                </td>
              </tr>
            ) : (
              jobs.map((job) => (
                <tr key={job.JobId} className="border-b hover:bg-gray-50">
                  <td className="border px-2 py-1 text-xs">
                    {formatDateShort(job.CreatedAt, userTimezone)}
                  </td>
                  <td className="border px-2 py-1">
                    <div className="flex gap-1">
                      {job.ThumbnailUrls && job.ThumbnailUrls.length > 0 ? (
                        job.ThumbnailUrls.slice(0, 3).map(
                          (thumbnailUrl, idx) => (
                            <AuthenticatedImage
                              key={idx}
                              src={thumbnailUrl}
                              alt="thumbnail"
                              className="cursor-pointer hover:opacity-80 transition-opacity"
                              style={{
                                width: 40,
                                height: 40,
                                objectFit: "cover",
                                borderRadius: 4,
                                border: "1px solid #ccc",
                              }}
                              onClick={() => openImageModal(job, idx)}
                              onError={(e) =>
                                (e.currentTarget.style.display = "none")
                              }
                            />
                          )
                        )
                      ) : (
                        <div className="w-10 h-10 bg-gray-100 border border-gray-300 rounded flex items-center justify-center">
                          <span className="text-gray-400 text-xs">
                            No image
                          </span>
                        </div>
                      )}
                      {job.ThumbnailUrls && job.ThumbnailUrls.length > 3 && (
                        <div className="w-10 h-10 bg-gray-100 border border-gray-300 rounded flex items-center justify-center">
                          <span className="text-gray-600 text-xs">
                            +{job.ThumbnailUrls.length - 3}
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="border px-2 py-1 text-xs">
                    <span
                      className={
                        job.ExtractedData?.Shipper?.Name
                          ? "text-gray-900"
                          : "text-gray-400"
                      }
                    >
                      {job.ExtractedData?.Shipper?.Name || "Not extracted"}
                    </span>
                  </td>
                  <td className="border px-2 py-1 text-xs">
                    <span
                      className={
                        job.EdiInterchangeSenderId
                          ? "text-gray-900"
                          : "text-gray-400"
                      }
                    >
                      {job.EdiInterchangeSenderId || "Not set"}
                    </span>
                  </td>
                  <td className="border px-2 py-1 text-xs">
                    <span
                      className={
                        job.EdiInterchangeReceiverId
                          ? "text-gray-900"
                          : "text-gray-400"
                      }
                    >
                      {job.EdiInterchangeReceiverId || "Not set"}
                    </span>
                  </td>
                  <td className="border px-2 py-1 text-xs">
                    <span
                      className={
                        job.ProcessingType ? "text-gray-900" : "text-gray-400"
                      }
                    >
                      {job.ProcessingType || "Not generated"}
                    </span>
                  </td>
                  <td className="border px-2 py-1 text-xs">
                    <span
                      className={
                        job?.ISAControlNumber
                          ? "text-gray-900"
                          : "text-gray-400"
                      }
                    >
                      {job?.ISAControlNumber || "Not extracted"}
                    </span>
                  </td>
                  <td className="border px-2 py-1">
                    <button
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                      onClick={() => openModal(job)}
                      title="View Details"
                    >
                      <Eye className="h-4 w-4 text-gray-600" />
                    </button>
                  </td>
                  <td className="border px-2 py-1 text-xs">
                    <div className="flex items-center gap-1">
                      {statusIcon(job.StatusIcon, job.Status)}
                      <span
                        className={
                          job.StatusIcon === ProcessingStatusIconEnum.Completed
                            ? "text-green-600"
                            : job.StatusIcon === ProcessingStatusIconEnum.Failed
                            ? "text-red-600"
                            : job.StatusIcon ===
                              ProcessingStatusIconEnum.InProgress
                            ? "text-blue-600"
                            : job.StatusIcon ===
                              ProcessingStatusIconEnum.ReadyForReview
                            ? "text-amber-700"
                            : job.StatusIcon ===
                              ProcessingStatusIconEnum.InReview
                            ? "text-violet-700"
                            : "text-gray-500"
                        }
                      >
                        {job.StatusIcon === ProcessingStatusIconEnum.Failed
                          ? `${formatJobStatusDisplay(job)} [No charge]`
                          : formatJobStatusDisplay(job)}
                      </span>
                      {job.StatusIcon === ProcessingStatusIconEnum.Failed &&
                        job.ErrorMessage && (
                          <span
                            className="ml-1"
                            title={job.ErrorMessage}
                            style={{ cursor: "help", color: "red" }}
                          >
                            🛈
                          </span>
                        )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4">
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border p-6 text-center text-gray-500">
            <div className="flex items-center justify-center">
              <svg
                className="animate-spin"
                width="32"
                height="32"
                viewBox="0 0 24 24"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="#007bff"
                  strokeWidth="4"
                  fill="none"
                  opacity="0.2"
                />
                <path
                  d="M12 2a10 10 0 0 1 10 10"
                  stroke="#007bff"
                  strokeWidth="4"
                  fill="none"
                />
              </svg>
              <span className="ml-2">Loading documents...</span>
            </div>
          </div>
        ) : jobs.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border p-6 text-center text-gray-500">
            No processed documents found. Upload some bills of lading to get
            started.
          </div>
        ) : (
          jobs.map((job) => (
            <div
              key={job.JobId}
              className="bg-white rounded-lg shadow-sm border p-4"
            >
              {/* Header with Status and Timestamp */}
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                  {statusIcon(job.StatusIcon, job.Status)}
                  <span
                    className={
                      job.StatusIcon === ProcessingStatusIconEnum.Completed
                        ? "text-green-600 font-medium"
                        : job.StatusIcon === ProcessingStatusIconEnum.Failed
                        ? "text-red-600 font-medium"
                        : job.StatusIcon === ProcessingStatusIconEnum.InProgress
                        ? "text-blue-600 font-medium"
                        : job.StatusIcon ===
                          ProcessingStatusIconEnum.ReadyForReview
                        ? "text-amber-700 font-medium"
                        : job.StatusIcon === ProcessingStatusIconEnum.InReview
                        ? "text-violet-700 font-medium"
                        : "text-gray-500 font-medium"
                    }
                  >
                    {job.StatusIcon === ProcessingStatusIconEnum.Failed
                      ? `${formatJobStatusDisplay(job)} [No charge]`
                      : formatJobStatusDisplay(job)}
                  </span>
                  {job.StatusIcon === ProcessingStatusIconEnum.Failed &&
                    job.ErrorMessage && (
                      <span
                        className="ml-1"
                        title={job.ErrorMessage}
                        style={{ cursor: "help", color: "red" }}
                      >
                        🛈
                      </span>
                    )}
                </div>
                <span className="text-sm text-gray-500">
                  {formatDateShort(job.CreatedAt, userTimezone)}
                </span>
              </div>

              {/* Thumbnails */}
              <div className="mb-3">
                <div className="flex gap-2">
                  {job.ThumbnailUrls && job.ThumbnailUrls.length > 0 ? (
                    job.ThumbnailUrls.slice(0, 4).map((thumbnailUrl, idx) => (
                      <AuthenticatedImage
                        key={idx}
                        src={thumbnailUrl}
                        alt="thumbnail"
                        className="cursor-pointer hover:opacity-80 transition-opacity"
                        style={{
                          width: 60,
                          height: 60,
                          objectFit: "cover",
                          borderRadius: 6,
                          border: "1px solid #ccc",
                        }}
                        onClick={() => openImageModal(job, idx)}
                        onError={(e) =>
                          (e.currentTarget.style.display = "none")
                        }
                      />
                    ))
                  ) : (
                    <div
                      className="w-15 h-15 bg-gray-100 border border-gray-300 rounded flex items-center justify-center"
                      style={{ width: 60, height: 60 }}
                    >
                      <span className="text-gray-400 text-xs">No image</span>
                    </div>
                  )}
                  {job.ThumbnailUrls && job.ThumbnailUrls.length > 4 && (
                    <div
                      className="w-15 h-15 bg-gray-100 border border-gray-300 rounded flex items-center justify-center"
                      style={{ width: 60, height: 60 }}
                    >
                      <span className="text-gray-600 text-xs">
                        +{job.ThumbnailUrls.length - 4}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Information Grid */}
              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">
                    Shipper:
                  </span>
                  <span
                    className={`text-sm ${
                      job.ExtractedData?.Shipper?.Name
                        ? "text-gray-900"
                        : "text-gray-400"
                    }`}
                  >
                    {job.ExtractedData?.Shipper?.Name || "Not extracted"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">
                    Sender ID:
                  </span>
                  <span
                    className={`text-sm ${
                      job.EdiInterchangeSenderId
                        ? "text-gray-900"
                        : "text-gray-400"
                    }`}
                  >
                    {job.EdiInterchangeSenderId || "Not set"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">
                    Receiver ID:
                  </span>
                  <span
                    className={`text-sm ${
                      job.EdiInterchangeReceiverId
                        ? "text-gray-900"
                        : "text-gray-400"
                    }`}
                  >
                    {job.EdiInterchangeReceiverId || "Not set"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">
                    EDI Type:
                  </span>
                  <span
                    className={`text-sm ${
                      job.ProcessingType ? "text-gray-900" : "text-gray-400"
                    }`}
                  >
                    {job.ProcessingType || "Not generated"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">
                    ISA:
                  </span>
                  <span
                    className={`text-sm ${
                      job?.ISAControlNumber ? "text-gray-900" : "text-gray-400"
                    }`}
                  >
                    {job?.ISAControlNumber || "Not extracted"}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end">
                <button
                  className="p-2 hover:bg-gray-100 rounded transition-colors"
                  onClick={() => openModal(job)}
                  title="View Details"
                >
                  <Eye className="h-4 w-4 text-gray-600" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      <div className="flex justify-between items-center mt-4">
        <Button
          size="sm"
          disabled={page === 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          Prev
        </Button>
        <span>
          Page {page} of {totalPages || 1}
        </span>
        <Button
          size="sm"
          disabled={page === totalPages || totalPages === 0}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        >
          Next
        </Button>
      </div>

      {modalOpen && selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
            <button
              className="absolute top-2 right-2 text-xl font-bold hover:text-red-500"
              onClick={closeModal}
            >
              &times;
            </button>
            <h2 className="text-xl font-bold mb-4">
              Details for {selectedJob.FileName}
            </h2>

            {/* Job Information */}
            <div className="mb-4 grid grid-cols-2 gap-4">
              <div>
                <h3 className="font-semibold">Job ID:</h3>
                <p className="text-sm text-gray-600">{selectedJob.JobId}</p>
              </div>
              <div>
                <h3 className="font-semibold">Status:</h3>
                <div className="flex items-center gap-2">
                  {statusIcon(selectedJob.StatusIcon, selectedJob.Status)}
                  <span>{selectedJob.Status}</span>
                </div>
              </div>
              <div>
                <h3 className="font-semibold">EDI Type:</h3>
                <p className="text-sm text-gray-600">
                  {selectedJob.ProcessingType || "Not generated"}
                </p>
              </div>
              <div>
                <h3 className="font-semibold">ISA Control Number:</h3>
                <p className="text-sm text-gray-600">
                  {selectedJob.ISAControlNumber || "Not available"}
                </p>
              </div>
              <div>
                <h3 className="font-semibold">Sender ID:</h3>
                <p className="text-sm text-gray-600">
                  {selectedJob.EdiInterchangeSenderId || "Not set"}
                </p>
              </div>
              <div>
                <h3 className="font-semibold">Receiver ID:</h3>
                <p className="text-sm text-gray-600">
                  {selectedJob.EdiInterchangeReceiverId || "Not set"}
                </p>
              </div>
              <div>
                <h3 className="font-semibold">Created:</h3>
                <p className="text-sm text-gray-600">
                  {formatDateShort(selectedJob.CreatedAt, userTimezone)}
                </p>
              </div>
              <div>
                <h3 className="font-semibold">Completed:</h3>
                <p className="text-sm text-gray-600">
                  {selectedJob.CompletedAt
                    ? formatDateShort(selectedJob.CompletedAt, userTimezone)
                    : "Not completed"}
                </p>
              </div>
            </div>

            {/* Images and EDI Content Side by Side */}
            {((selectedJob.DisplayUrls && selectedJob.DisplayUrls.length > 0) ||
              selectedJob.EDIContent) && (
              <div className="mb-4 flex gap-6">
                {/* Images Section */}
                {selectedJob.DisplayUrls &&
                  selectedJob.DisplayUrls.length > 0 && (
                    <div className="flex-1">
                      <h3 className="font-semibold mb-2">Images:</h3>
                      <div className="flex gap-4 flex-wrap">
                        {selectedJob.DisplayUrls.map((url, idx) => (
                          <AuthenticatedImage
                            key={idx}
                            src={url}
                            alt="display"
                            style={{
                              maxHeight: 300,
                              maxWidth: 400,
                              borderRadius: 4,
                              border: "1px solid #ccc",
                            }}
                            onError={(e) => {
                              // Fallback to original image if display image fails
                              if (
                                selectedJob.ImageUrls &&
                                selectedJob.ImageUrls[idx]
                              ) {
                                e.currentTarget.src =
                                  selectedJob.ImageUrls[idx];
                              }
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                {/* EDI Content Section */}
                {selectedJob.EDIContent && (
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold">Generated EDI Content:</h3>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            copyToClipboard(selectedJob.EDIContent!)
                          }
                          title="Copy to clipboard"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadEdi(selectedJob)}
                          title="Download EDI as text file"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <textarea
                      className="w-full bg-gray-100 p-3 rounded border text-xs font-mono resize-none"
                      style={{ height: 300 }}
                      value={selectedJob.EDIContent}
                      readOnly
                    />
                  </div>
                )}
              </div>
            )}

            {/* Original Images Links */}
            {selectedJob.ImageUrls && selectedJob.ImageUrls.length > 0 && (
              <div className="mb-4">
                <h3 className="font-semibold mb-2">Original Images:</h3>
                <div className="flex gap-2 flex-wrap">
                  {selectedJob.ImageUrls.map((url, idx) => (
                    <a
                      key={idx}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded hover:bg-blue-200 transition-colors"
                    >
                      Original Image {idx + 1}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Extracted Data */}
            {selectedJob.ExtractedData && (
              <div className="mb-4">
                <h3 className="font-semibold mb-2">Extracted Data:</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {selectedJob.ExtractedData.Shipper && (
                    <div>
                      <h4 className="font-medium">Shipper:</h4>
                      <p>{selectedJob.ExtractedData.Shipper.Name || "N/A"}</p>
                      <p>{selectedJob.ExtractedData.Shipper.Address || ""}</p>
                      <p>
                        {selectedJob.ExtractedData.Shipper.City || ""}{" "}
                        {selectedJob.ExtractedData.Shipper.State || ""}{" "}
                        {selectedJob.ExtractedData.Shipper.Zip || ""}
                      </p>
                    </div>
                  )}
                  {selectedJob.ExtractedData.Consignee && (
                    <div>
                      <h4 className="font-medium">Consignee:</h4>
                      <p>{selectedJob.ExtractedData.Consignee.Name || "N/A"}</p>
                      <p>{selectedJob.ExtractedData.Consignee.Address || ""}</p>
                      <p>
                        {selectedJob.ExtractedData.Consignee.City || ""}{" "}
                        {selectedJob.ExtractedData.Consignee.State || ""}{" "}
                        {selectedJob.ExtractedData.Consignee.Zip || ""}
                      </p>
                    </div>
                  )}
                  {selectedJob.ExtractedData.References && (
                    <div>
                      <h4 className="font-medium">References:</h4>
                      <p>
                        BOL:{" "}
                        {selectedJob.ExtractedData.References.BolNumber ||
                          "N/A"}
                      </p>
                      <p>
                        PO:{" "}
                        {selectedJob.ExtractedData.References.PoNumber || "N/A"}
                      </p>
                      <p>
                        ISA: {selectedJob.ExtractedData.References.ISA || "N/A"}
                      </p>
                    </div>
                  )}
                  {selectedJob.ExtractedData.ShipmentDetails && (
                    <div>
                      <h4 className="font-medium">Shipment Details:</h4>
                      <p>
                        Weight:{" "}
                        {selectedJob.ExtractedData.ShipmentDetails.Weight ||
                          "N/A"}
                      </p>
                      <p>
                        Pieces:{" "}
                        {selectedJob.ExtractedData.ShipmentDetails.Pieces ||
                          "N/A"}
                      </p>
                      <p>
                        Commodity:{" "}
                        {selectedJob.ExtractedData.ShipmentDetails.Commodity ||
                          "N/A"}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Validation Errors */}
            <div className="mb-2">
              <h4 className="font-semibold">Validation Errors:</h4>
              <ul className="list-disc ml-6 text-red-600">
                {(selectedJob.ExtractedData?.Validation || []).map(
                  (v: string, i: number) => (
                    <li key={i}>{v}</li>
                  )
                )}
                {(!selectedJob.ExtractedData?.Validation ||
                  selectedJob.ExtractedData?.Validation.length === 0) && (
                  <li className="text-green-600">None</li>
                )}
              </ul>
            </div>

            {/* Warnings */}
            <div className="mb-2">
              <h4 className="font-semibold">Warnings:</h4>
              <ul className="list-disc ml-6 text-yellow-600">
                {(selectedJob.ExtractedData?.Warnings || []).map(
                  (w: string, i: number) => (
                    <li key={i}>{w}</li>
                  )
                )}
                {(!selectedJob.ExtractedData?.Warnings ||
                  selectedJob.ExtractedData?.Warnings.length === 0) && (
                  <li className="text-green-600">None</li>
                )}
              </ul>
            </div>

            {/* Missing Fields */}
            <div className="mb-2">
              <h4 className="font-semibold">Missing Fields:</h4>
              <ul className="list-disc ml-6 text-gray-600">
                {(selectedJob.ExtractedData?.MissingFields || []).map(
                  (m: string, i: number) => (
                    <li key={i}>{m}</li>
                  )
                )}
                {(!selectedJob.ExtractedData?.MissingFields ||
                  selectedJob.ExtractedData?.MissingFields.length === 0) && (
                  <li className="text-green-600">None</li>
                )}
              </ul>
            </div>

            {/* Error Message */}
            {selectedJob.ErrorMessage && (
              <div className="mb-4">
                <h4 className="font-semibold text-red-600">Error:</h4>
                <p className="text-red-600 text-sm">
                  {selectedJob.ErrorMessage}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Image Modal */}
      {imageModalOpen && selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="relative max-w-4xl max-h-screen p-4">
            <button
              className="absolute top-2 right-2 text-white text-2xl font-bold hover:text-gray-300 z-10"
              onClick={closeImageModal}
            >
              &times;
            </button>
            <AuthenticatedImage
              src={
                selectedJob.DisplayUrls?.[selectedImageIndex] ||
                selectedJob.ImageUrls?.[selectedImageIndex]
              }
              alt={`Display image ${selectedImageIndex + 1}`}
              className="max-w-full max-h-screen object-contain"
              onError={(e) => {
                // Fallback to original image if display image fails
                if (
                  selectedJob.ImageUrls &&
                  selectedJob.ImageUrls[selectedImageIndex]
                ) {
                  e.currentTarget.src =
                    selectedJob.ImageUrls[selectedImageIndex];
                }
              }}
            />
            {(selectedJob.DisplayUrls?.length > 1 ||
              selectedJob.ImageUrls?.length > 1) && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                {(selectedJob.DisplayUrls || selectedJob.ImageUrls).map(
                  (_, idx) => (
                    <button
                      key={idx}
                      className={`w-3 h-3 rounded-full ${
                        idx === selectedImageIndex ? "bg-white" : "bg-gray-400"
                      }`}
                      onClick={() => setSelectedImageIndex(idx)}
                    />
                  )
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents;
